import type { PropertyImage } from "@/types/property";

// Display-only placeholder image for a property that has no uploaded photo.
//
// This is NEVER stored — the frontend simply points a card's <img> at a seeded
// external image so image-less listings don't render as an empty gray box. A
// real upload (which goes to R2) always takes precedence and replaces it.
//
// Seeded by the property id so each property keeps a stable, distinct image
// (and it matches the MSW dev fixtures, which use the same picsum seed). This
// is the single place the placeholder source lives — swap the return here to
// change every card at once (e.g. to bundled real-estate photos later).
export function propertyPlaceholderImageUrl(id: number): string {
  return `https://picsum.photos/seed/property-${id}/400/300`;
}

/**
 * A synthetic {@link PropertyImage} gallery (default 3) for a property with no
 * uploaded photos, so the detail carousel always looks full. Never stored — a
 * real upload replaces the whole set. Image #1 reuses the card's seed so the
 * card thumbnail and the detail hero show the same photo; the rest get distinct
 * seeds for variety. Ids are negative so they never collide with real images.
 */
export function placeholderGalleryImages(
  propertyId: number,
  count = 3,
): PropertyImage[] {
  return Array.from({ length: count }, (_, i) => {
    const seed =
      i === 0 ? `property-${propertyId}` : `property-${propertyId}-${i + 1}`;
    return {
      id: -(i + 1),
      propertyId,
      originalFilename: "",
      mimeType: "image/jpeg",
      sizeBytes: 0,
      displayOrder: i,
      watermarkApplied: false,
      thumbnailUrl: `https://picsum.photos/seed/${seed}/400/300`,
      fullUrl: `https://picsum.photos/seed/${seed}/1200/900`,
      directUrl: `https://picsum.photos/seed/${seed}/1200/900`,
      createdAt: "",
    };
  });
}
